// src/pages/AddProducts.jsx
import React, { useState, useEffect, useRef } from "react";
import MasterLayout from "../Sections/MasterLayout";
import Loader from "./Loader";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import toast from "react-hot-toast";
import {
  X,
  GripHorizontal,
  Image as ImageIcon,
  Plus,
  Search,
  ChevronDown,
  ChevronUp,
  Edit3,
  Check,
} from "lucide-react";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";

// CKEditor
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

import {
  save_product,
  get_product_data,
  server_post_json,
  get_all_collections,
  server_post_data,
} from "../../ServiceConnection/serviceconnection";

// Predefined Colors with Hex Codes

const PREDEFINED_COLORS = [
  { name: "White", hex: "#FFFFFF" },
  { name: "Off White", hex: "#fef8e1" },
  { name: "Black", hex: "#151515" },
  { name: "Navy Blue", hex: "#000a1a" },
  { name: "Royal Blue", hex: "#141c4f" },
  { name: "Baby Blue", hex: "#abebff" },
  { name: "Mint", hex: "#adfff0" },
  { name: "Baby Pink", hex: "#ffd4e9" },
  { name: "Light Baby Pink", hex: "#ffd4e9" },
  { name: "Lavender", hex: "#e0d2fc" },
  { name: "Grey Melange", hex: "#B0B0B0" },
  { name: "Grey", hex: "#808080" },
  { name: "Bottle Green", hex: "#083717" },
  { name: "Olive Green", hex: "#26260a" },
  { name: "Green", hex: "#008000" },
  { name: "Red", hex: "#900001" },
  { name: "Maroon", hex: "#800000" },
  { name: "Golden Yellow", hex: "#ffa200" },
  { name: "Orange", hex: "#FF8C00" },
  { name: "Coral", hex: "#b34946" },
];
// Sortable Images Component
function SortableProductImage({ id, url, onRemove, isFirst, isViewMode }) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative group flex-shrink-0 ${isFirst ? "w-32 h-48 sm:w-40 sm:h-60" : "w-20 h-20 sm:w-24 sm:h-24"
        }`}
    >
      <img
        src={url}
        alt=""
        className="w-full h-full object-cover rounded-lg border border-gray-200"
      />
      {!isViewMode && (
        <button
          onClick={() => onRemove(id)}
          className="absolute top-1 right-1 bg-white rounded-full p-1 shadow opacity-0 group-hover:opacity-100"
        >
          <X className="w-4 h-4 text-gray-600" />
        </button>
      )}
      <div
        {...attributes}
        {...listeners}
        className="absolute bottom-1 left-1 cursor-move bg-white rounded p-1 shadow"
      >
        <GripHorizontal className="w-4 h-4 text-gray-500" />
      </div>
      {isFirst && (
        <span className="absolute top-1 left-1 bg-gray-900 text-white text-xs px-2 py-0.5 rounded">
          Main
        </span>
      )}
    </div>
  );
}

function MediaLibraryImage({
  image,
  isSelected,
  onToggle,
  showSelection = false,
  isViewMode
}) {
  return (
    <div className="relative group w-20 h-20 sm:w-24 sm:h-24">
      <img
        src={image.url}
        alt=""
        className="w-full h-full object-cover rounded-lg border border-gray-200"
      />

      {!isViewMode && showSelection && (
        <button
          onClick={() => onToggle(image.id)}
          className={`absolute top-1 right-1 w-6 h-6 rounded-full flex items-center justify-center ${isSelected ? "bg-blue-600 text-white" : "bg-white text-gray-600"
            }`}
        >
          {isSelected ? (
            <Check className="w-3 h-3" />
          ) : (
            <Plus className="w-3 h-3" />
          )}
        </button>
      )}
    </div>
  );
}

export default function AddProducts() {
  const navigate = useNavigate();
  const { id: product_id } = useParams();
  const location = useLocation();
  const isViewMode = location.pathname.startsWith("/product/");

  // ====== All State ======
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [compareAtPrice, setCompareAtPrice] = useState("");
  const [vendor, setVendor] = useState("LazyX");
  const [collections, setCollections] = useState([]);
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState("");

  const [pageTitle, setPageTitle] = useState("");
  const [metaDescription, setMetaDescription] = useState("");
  const [urlHandle, setUrlHandle] = useState("");
  const [seoEditOpen, setSeoEditOpen] = useState(false);

  // Images
  const [allImages, setAllImages] = useState([]);
  const [mainImages, setMainImages] = useState([]);
  const [variantImageAssignments, setVariantImageAssignments] = useState({});
  const [sizeChart, setSizeChart] = useState(null);

  // Variants
  const [hasVariants, setHasVariants] = useState(false);
  const [colorInput, setColorInput] = useState("");
  const [colors, setColors] = useState([]); // [{id, name}]
  const predefinedSizes = ["XS", "S", "M", "L", "XL", "XXL"];
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [expandedColors, setExpandedColors] = useState({});

  const [variantData, setVariantData] = useState({});

  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("main");
  const [currentColorId, setCurrentColorId] = useState(null);
  const [selectedMedia, setSelectedMedia] = useState([]);

  // Collections dropdown
  const [showCollectionDropdown, setShowCollectionDropdown] = useState(false);
  const [collectionSearch, setCollectionSearch] = useState("");
  const [existingSizeChart, setExistingSizeChart] = useState(null);

  // For highlighting tags that match collection conditions
  const [allCollections, setAllCollections] = useState([]);
  const [matchedConditionTags, setMatchedConditionTags] = useState(new Set());
  const [customColorName, setCustomColorName] = useState("");
  const [customColorHex, setCustomColorHex] = useState("#000000");
  // ====== Sensors for Drag & Drop ======
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // ====== Auto SEO ======
  useEffect(() => {
    if (!title) return;
    setPageTitle(title.substring(0, 70));
    const slug = title
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "")
      .replace(/--+/g, "-");
    setUrlHandle(slug);
  }, [title]);

  useEffect(() => {
    if (description) {
      setMetaDescription(description.substring(0, 160));
    }
  }, [description]);

  // Fetch collections for tag matching
  useEffect(() => {
    const fetchCollections = async () => {
      try {
        const res = await server_post_json(get_all_collections, { fast: true });
        const collections = res?.data?.data || [];
        setAllCollections(collections);
        const conditionTags = new Set();
        collections.forEach((col) => {
          (col.conditions || []).forEach((cond) => {
            if (cond.value) {
              conditionTags.add(cond.value.trim().toLowerCase());
            }
          });
        });
        setMatchedConditionTags(conditionTags);
      } catch (err) {
        console.error("Failed to load collections for tag matching", err);
      }
    };

    fetchCollections();
  }, []);

  // ====== Fetch Product for Edit ======
  const fetchProduct = async () => {
    if (!product_id && !isViewMode) return;

    try {
      setLoading(true);

      const res = await server_post_json(get_product_data, {
        product_id: isViewMode
          ? location.pathname.split("/").pop()
          : product_id,
      });
      const product = res?.data?.product;

      if (!product) {
        toast.error("Product not found");
        return;
      }

      setTitle(product.title || "");
      setUrlHandle(product.slug || "");
      setDescription(product.description || "");
      setTags(product.tags || []);
      setCollections(product.collections?.map((c) => c.title) || []);

      setPrice(product.price || "");
      setCompareAtPrice(product.compareAtPrice || "");

      if (product.size_chart) {
        setExistingSizeChart(product.size_chart);
      }

      const images = [];
      const mainIds = [];

      (product.mainImages || []).forEach((url, i) => {
        const id = `main-${i}`;
        images.push({ id, url, file: null });
        mainIds.push(id);
      });

      if (Array.isArray(product.variants) && product.variants.length) {
        setHasVariants(true);

        const colorList = [];
        const variantImgs = {};
        const vData = {};
        const sizeSet = new Set();

        product.variants.forEach((group, idx) => {
          const colorId = `color-${idx}`;

          colorList.push({
            id: colorId,
            name: group.color,
          });

          variantImgs[colorId] = [];

          (group.images || []).forEach((img, i) => {
            const imgId = `variant-${idx}-${i}`;
            images.push({ id: imgId, url: img, file: null });
            variantImgs[colorId].push(imgId);
          });

          group.variants.forEach((v) => {
            sizeSet.add(v.size);
            vData[`${colorId}-${v.size}`] = {
              available: v.available,
              code: v.code,
            };
          });
        });

        setAllImages(images);
        setMainImages(mainIds);
        setColors(colorList);
        setVariantImageAssignments(variantImgs);
        setVariantData(vData);
        setSelectedSizes([...sizeSet]);

        const expanded = {};
        colorList.forEach((c) => (expanded[c.id] = true));
        setExpandedColors(expanded);
      } else {
        setHasVariants(false);
        setAllImages(images);
        setMainImages(mainIds);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load product");
    } finally {
      setLoading(false);
    }
  };

  const disabledProps = isViewMode ? { disabled: true, readOnly: true } : {};

  useEffect(() => {
    if (product_id || isViewMode) {
      fetchProduct();
    }
  }, [product_id, isViewMode]);

  // ====== Image Handlers ======
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const newImages = [];

    files.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const newImage = {
          id: `upload-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          url: reader.result,
          file,
        };
        newImages.push(newImage);

        setAllImages((prev) => [...prev, newImage]);

        if (!modalOpen || modalMode === "main") {
          setMainImages((prev) => [...prev, newImage.id]);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (id) => {
    setAllImages((prev) => prev.filter((img) => img.id !== id));
    setMainImages((prev) => prev.filter((imgId) => imgId !== id));

    setVariantImageAssignments((prev) => {
      const newAssignments = { ...prev };
      Object.keys(newAssignments).forEach((colorId) => {
        newAssignments[colorId] = newAssignments[colorId].filter(
          (imgId) => imgId !== id
        );
      });
      return newAssignments;
    });
  };

  const getImageById = (id) => allImages.find((img) => img.id === id);

  // ====== Media Modal Functions ======
  const openMediaModal = (mode, colorId = null) => {
    setModalMode(mode);
    setCurrentColorId(colorId);

    if (mode === "main") {
      setSelectedMedia([...mainImages]);
    } else if (mode === "variant" && colorId) {
      setSelectedMedia([...(variantImageAssignments[colorId] || [])]);
    }

    setModalOpen(true);
  };

  const handleMediaSelection = (imageId) => {
    setSelectedMedia((prev) => {
      if (prev.includes(imageId)) {
        return prev.filter((id) => id !== imageId);
      } else {
        return [...prev, imageId];
      }
    });
  };

  const saveMediaSelection = () => {
    if (modalMode === "main") {
      setMainImages(selectedMedia);
    } else if (modalMode === "variant" && currentColorId) {
      setVariantImageAssignments((prev) => ({
        ...prev,
        [currentColorId]: selectedMedia,
      }));
    }
    setModalOpen(false);
    setSelectedMedia([]);
  };

  // ====== Color & Size Management ======
  const addColor = (colorName, hex = null) => {
    const trimmed = colorName.trim();
    if (
      !trimmed ||
      colors.some((c) => c.name.toLowerCase() === trimmed.toLowerCase())
    )
      return;

    const newColor = {
      id: Date.now().toString(),
      name: trimmed,
      hex: hex || "#000000", // 🔥 custom hex support
    };

    setColors((prev) => [...prev, newColor]);
    setExpandedColors((prev) => ({ ...prev, [newColor.id]: true }));
  };

  const removeColor = (id) => {
    setColors((prev) => prev.filter((c) => c.id !== id));
    setVariantImageAssignments((prev) => {
      const { [id]: _, ...rest } = prev;
      return rest;
    });
  };

  const toggleSize = (size) => {
    setSelectedSizes((prev) =>
      prev.includes(size) ? prev.filter((s) => s !== size) : [...prev, size]
    );
  };

  const updateVariantField = (key, field, value) => {
    setVariantData((prev) => ({
      ...prev,
      [key]: { ...prev[key] || {}, [field]: value },
    }));
  };

  const getColorInventory = (colorId) => {
    return selectedSizes.reduce(
      (sum, size) =>
        sum + parseInt(variantData[`${colorId}-${size}`]?.available || 0, 10),
      0
    );
  };

  // ====== Collections & Tags ======
  const dummyCollections = [
    "ACID WASH T-SHIRT",
    "best-sellers",
    "new-arrival",
    "OVERSIZED T-SHIRTS",
    "tie-dye",
    "unisex",
  ];
  const filteredCollections = dummyCollections.filter((c) =>
    c.toLowerCase().includes(collectionSearch.toLowerCase())
  );

  const addCollection = (col) => {
    if (!collections.includes(col)) setCollections((prev) => [...prev, col]);
    setCollectionSearch("");
    setShowCollectionDropdown(false);
  };

  const addNewCollection = () => {
    const newCol = collectionSearch.trim();
    if (newCol && !collections.includes(newCol))
      setCollections((prev) => [...prev, newCol]);
    setCollectionSearch("");
    setShowCollectionDropdown(false);
  };

  const removeCollectionItem = (col) =>
    setCollections((prev) => prev.filter((c) => c !== col));

  const addTag = () => {
    const newTag = tagInput.trim().toLowerCase();
    if (newTag && !tags.includes(newTag)) setTags((prev) => [...prev, newTag]);
    setTagInput("");
  };

  const removeTag = (tag) => setTags((prev) => prev.filter((t) => t !== tag));

  const validateProductForm = () => {
    if (!title.trim()) {
      toast.error("Title is required");
      return false;
    }

    if (!price) {
      toast.error("Price is required");
      return false;
    }

    if (hasVariants) {
      if (colors.length === 0 || selectedSizes.length === 0) {
        toast.error("Add at least one color and one size for variants");
        return false;
      }

      const filledSkus = Object.values(variantData)
        .map((v) => v.code?.trim())
        .filter(Boolean);

      if (new Set(filledSkus).size !== filledSkus.length) {
        toast.error("Duplicate SKU found");
        return false;
      }
    }

    return true;
  };

  // ====== Save Product ======
  const handleSaveProduct = async () => {
    if (!validateProductForm()) return;

    try {
      setLoading(true);

      const formData = new FormData();

      if (product_id) formData.append("product_id", product_id);

      formData.append("title", title);
      formData.append("description", description);
      formData.append("price", price);
      formData.append("compareAtPrice", compareAtPrice);
      formData.append("tags", JSON.stringify(tags));
      formData.append("pageTitle", pageTitle);
      formData.append("metaDescription", metaDescription);
      formData.append("slug", urlHandle);

      formData.append("hasVariants", hasVariants);
      if (sizeChart) {
        formData.append("size_chart", sizeChart);
      }

      // Handle main images order and new files
      const newMainFiles = [];
      const mainOrder = [];
      mainImages.forEach((id) => {
        const img = getImageById(id);
        if (img) {
          if (img.file) {
            newMainFiles.push(img.file);
            mainOrder.push(`new:${newMainFiles.length - 1}`);
          } else if (img.url) {
            mainOrder.push(img.url);
          }
        }
      });
      formData.append("mainImagesOrder", JSON.stringify(mainOrder));
      newMainFiles.forEach((file) => {
        formData.append("mainImages", file);
      });

      if (hasVariants) {
        // Handle variant images similarly
        const variantNewFilesGroups = []; // array of arrays, one per color
        const variantsPayload = colors.map((c) => {
          const colorIndex = colors.indexOf(c);
          variantNewFilesGroups[colorIndex] = [];
          const images = (variantImageAssignments[c.id] || []).map((id) => {
            const img = getImageById(id);
            if (img) {
              if (img.file) {
                variantNewFilesGroups[colorIndex].push(img.file);
                return `new:${variantNewFilesGroups[colorIndex].length - 1}`;
              } else if (img.url) {
                return img.url;
              }
            }
            return null;
          }).filter(Boolean);

          return {
            color: c.name,
            images,
            variants: selectedSizes.map((s) => {
              const key = `${c.id}-${s}`;
              const v = variantData[key] || {};
              return {
                size: s,
                available: v.available || 0,
                code: v.code || "",
              };
            }),
          };
        });

        formData.append("variants", JSON.stringify(variantsPayload));

        // Append variant images files grouped by color index
        variantNewFilesGroups.forEach((files, colorIndex) => {
          files.forEach((file) => {
            formData.append(`variantImages${colorIndex}`, file);
          });
        });
      }

      await server_post_data(save_product, formData);

      toast.success("Product saved successfully");
      setTimeout(() => navigate("/allproducts"), 800);
    } catch (err) {
      console.error(err);
      toast.error("Failed to save product");
    } finally {
      setLoading(false);
    }
  };

  return (
    <MasterLayout>
      {loading && <Loader />}
      <div className="bg-gray-50 pb-0">
        <div className="mx-auto py-5 px-4 max-w-7xl">
          <h2 className="text-2xl font-bold mb-6">
            {isViewMode
              ? "Product Details"
              : product_id
                ? "Edit Product"
                : "Add Product"}
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Title & Description */}
              <div className="bg-white rounded-xl shadow-sm border p-5">
                <label className="block text-sm font-medium mb-2">Title</label>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg"
                  placeholder="Unisex Tie Dye Oversized T-Shirt"
                  {...disabledProps}
                />

                <label className="block text-sm font-medium mt-5 mb-2">
                  Description
                </label>
                {isViewMode ? (
                  <div
                    className="prose prose-sm max-w-none border rounded-lg p-4 bg-gray-50"
                    dangerouslySetInnerHTML={{ __html: description }}
                  />
                ) : (
                  <CKEditor
                    editor={ClassicEditor}
                    data={description}
                    onChange={(event, editor) =>
                      setDescription(editor.getData())
                    }
                    config={{
                      toolbar: [
                        "heading",
                        "|",
                        "bold",
                        "italic",
                        "link",
                        "bulletedList",
                        "numberedList",
                        "|",
                        "undo",
                        "redo",
                      ],
                    }}
                  />
                )}
              </div>

              {/* Media Section */}
              <div className="bg-white rounded-xl shadow-sm border p-5">
                <h3 className="font-medium mb-4">Media</h3>

                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={(e) => {
                    const { active, over } = e;
                    if (active.id !== over.id) {
                      setMainImages((items) => {
                        const oldIdx = items.findIndex((i) => i === active.id);
                        const newIdx = items.findIndex((i) => i === over.id);
                        return arrayMove(items, oldIdx, newIdx);
                      });
                    }
                  }}
                >
                  <SortableContext
                    items={mainImages}
                    strategy={horizontalListSortingStrategy}
                  >
                    <div className="flex gap-4 overflow-x-auto pb-6">
                      {mainImages.map((imgId, idx) => {
                        const img = getImageById(imgId);
                        return img ? (
                          <SortableProductImage
                            key={imgId}
                            id={imgId}
                            url={img.url}
                            onRemove={removeImage}
                            isFirst={idx === 0}
                            isViewMode={isViewMode}
                          />
                        ) : null;
                      })}

                      {!isViewMode && (
                        <div
                          onClick={() => openMediaModal("main")}
                          className="flex-shrink-0 w-20 h-20 sm:w-24 sm:h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center cursor-pointer hover:border-gray-400 bg-gray-50 transition"
                        >
                          <Plus className="w-8 h-8 text-gray-500" />
                        </div>
                      )}
                    </div>
                  </SortableContext>
                </DndContext>

                {mainImages.length === 0 && (
                  <p className="text-sm text-gray-500 mt-2">
                    Click the + button to add images. The first image will be
                    the main product image.
                  </p>
                )}
              </div>

              {/* Pricing */}
              <div className="bg-white rounded-xl shadow-sm border p-5">
                <h3 className="font-medium mb-4">Pricing</h3>
                <input
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder="Price"
                  className="w-full px-4 py-2 border rounded mb-3"
                  {...disabledProps}
                />
                <input
                  value={compareAtPrice}
                  onChange={(e) => setCompareAtPrice(e.target.value)}
                  placeholder="Compare at price"
                  className="w-full px-4 py-2 border rounded mb-3"
                  {...disabledProps}
                />
              </div>

              {/* Size Chart */}
              {/* <div className="bg-white rounded-xl shadow-sm border p-5">
                <h3 className="font-medium mb-3">Size Chart</h3>

                {sizeChart || existingSizeChart ? (
                  <div className="relative">
                    <img
                      src={
                        sizeChart
                          ? URL.createObjectURL(sizeChart)
                          : existingSizeChart
                      }
                      className="w-full h-40 object-contain border rounded"
                      alt="Size chart"
                    />
                    {!isViewMode && (
                      <button
                        onClick={() => {
                          setSizeChart(null);
                          setExistingSizeChart(null);
                        }}
                        className="absolute top-2 right-2 bg-white p-1 rounded shadow"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center h-32 border-2 border-dashed rounded cursor-pointer bg-gray-50">
                    <ImageIcon className="w-8 h-8 text-gray-400 mb-2" />
                    <span className="text-sm text-gray-600">
                      Upload size chart
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={(e) => setSizeChart(e.target.files[0])}
                    />
                  </label>
                )}
              </div> */}

              {/* Variants Section */}
              <div className="bg-white rounded-xl shadow-sm border p-5">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-medium">Variants</h3>
                  {!isViewMode && (
                    <button
                      onClick={() => setHasVariants(!hasVariants)}
                      className="text-blue-600 flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      {hasVariants ? "Remove variants" : "Add size/color options"}
                    </button>
                  )}
                </div>

                {hasVariants && (
                  <>
                    {/* ====== Improved Color Selection ====== */}
                    <div className="mb-8">
                      <span className="block text-sm font-medium mb-3">Color</span>

                      {/* Selected Colors Chips */}
                      <div className="flex flex-wrap gap-2 mb-5">
                        {colors.map((color) => {
                          const colorInfo =
                            PREDEFINED_COLORS.find(
                              (c) => c.name.toLowerCase() === color.name.toLowerCase()
                            ) || { hex: color.hex || "#000000" };

                          return (
                            <div
                              key={color.id}
                              className="bg-gray-100 px-3 py-1.5 rounded-full flex items-center gap-2 text-sm shadow-sm"
                            >
                              <div
                                className="w-5 h-5 rounded-full border border-gray-300"
                                style={{ backgroundColor: colorInfo.hex }}
                              />
                              {color.name}

                              {!isViewMode && (
                                <X
                                  className="w-4 h-4 cursor-pointer hover:text-red-600"
                                  onClick={() => removeColor(color.id)}
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Color Palette */}
                      {!isViewMode && (
                        <div>
                          <p className="text-xs text-gray-500 mb-3">
                            Click to select colors:
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {PREDEFINED_COLORS.map((colorItem) => {
                              const isSelected = colors.some(
                                (c) => c.name.toLowerCase() === colorItem.name.toLowerCase()
                              );

                              return (
                                <div key={colorItem.name} className="relative group">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (!isSelected) {
                                        addColor(colorItem.name);
                                      }
                                    }}
                                    disabled={isSelected}
                                    className={`
                relative w-10 h-10 rounded-full border-2 transition-all duration-200
                ${isSelected
                                        ? 'border-blue-600 scale-110 shadow-lg ring-2 ring-blue-300'
                                        : 'border-gray-200 hover:border-gray-400 hover:scale-110'
                                      }
              `}
                                    style={{ backgroundColor: colorItem.hex }}
                                  >
                                    {isSelected && (
                                      <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-full">
                                        <Check className="w-6 h-6 text-white drop-shadow-lg" strokeWidth={3} />
                                      </div>
                                    )}
                                  </button>

                                  {/* Hover name tooltip */}
                                  <div className="
              absolute -top-10 left-1/2 -translate-x-1/2 
              bg-gray-900 text-white text-[11px] 
              px-3 py-1.5 rounded-md 
              opacity-0 group-hover:opacity-100 
              pointer-events-none 
              transition-opacity duration-200
              whitespace-nowrap
              shadow-lg
              z-10
            ">
                                    {colorItem.name}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

{/* Custom Color Picker */}
{!isViewMode && (
  <div className="mt-6 border rounded-lg p-3 bg-gray-50">
    <p className="text-sm font-medium mb-2">Add Custom Color</p>

    <div className="flex flex-wrap items-center gap-2">
      
      {/* 📝 Name */}
      <input
        type="text"
        placeholder="Name"
        value={customColorName}
        onChange={(e) => setCustomColorName(e.target.value)}
        className="px-3 py-2 border rounded-lg text-sm w-full sm:w-40"
      />

      {/* 🎨 Picker */}
      <input
        type="color"
        value={customColorHex}
        onChange={(e) => setCustomColorHex(e.target.value)}
        className="h-10 w-14 border rounded-lg cursor-pointer p-1"
      />

      {/* 🔢 HEX */}
      <input
        type="text"
        value={customColorHex}
        onChange={(e) => setCustomColorHex(e.target.value)}
        className="px-3 py-2 border rounded-lg text-sm w-28"
      />

      {/* ➕ Button */}
      <button
        type="button"
        onClick={() => {
          if (!customColorName.trim()) {
            toast.error("Enter color name");
            return;
          }

          addColor(customColorName, customColorHex);
          setCustomColorName("");
          setCustomColorHex("#000000");
        }}
        className="px-4 py-2 bg-[var(--primary-color)] text-white rounded-lg text-sm font-medium whitespace-nowrap"
      >
        Add
      </button>
    </div>
  </div>
)}            </div>

                    {/* Sizes */}
                    <div className="mb-6">
                      <span className="font-medium block mb-3">Size</span>
                      <div className="flex flex-wrap gap-3">
                        {predefinedSizes.map((size) => (
                          <button
                            key={size}
                            onClick={() => toggleSize(size)}
                            className={`px-5 py-2 rounded-full text-sm font-medium ${selectedSizes.includes(size)
                                ? "bg-blue-600 text-white"
                                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                              }`}
                          >
                            {size}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Variant Groups */}
                    {colors.length > 0 && selectedSizes.length > 0 && (
                      <div className="space-y-3 mt-8">
                        {colors.map((color) => {
                          const colorInventory = getColorInventory(color.id);
                          const hasImages =
                            variantImageAssignments[color.id]?.length > 0;
                          const firstImage = hasImages
                            ? getImageById(variantImageAssignments[color.id][0])
                            : null;

                          const colorInfo = PREDEFINED_COLORS.find(
                            (c) => c.name.toLowerCase() === color.name.toLowerCase()
                          );

                          return (
                            <div
                              key={color.id}
                              className="border rounded-lg overflow-hidden"
                            >
                              <div className="flex items-center justify-between bg-gray-50 p-4">
                                <div className="flex items-center gap-4">
                                  <button
                                    onClick={() =>
                                      setExpandedColors((prev) => ({
                                        ...prev,
                                        [color.id]: !prev[color.id],
                                      }))
                                    }
                                  >
                                    {expandedColors[color.id] ? (
                                      <ChevronUp className="w-5 h-5" />
                                    ) : (
                                      <ChevronDown className="w-5 h-5" />
                                    )}
                                  </button>

                                  <div
                                    className="w-10 h-10 rounded-full border border-gray-300 shadow-sm"
                                    style={{ backgroundColor: colorInfo?.hex || "#000" }}
                                  />

                                  <div>
                                    <span className="font-medium block">{color.name}</span>
                                    <span className="text-sm text-gray-500">
                                      {selectedSizes.length} sizes
                                    </span>
                                  </div>

                                  <button
                                    onClick={() =>
                                      openMediaModal("variant", color.id)
                                    }
                                    className="ml-4 w-10 h-10 flex items-center justify-center border rounded bg-white hover:bg-gray-50"
                                  >
                                    {hasImages && firstImage ? (
                                      <img
                                        src={firstImage.url}
                                        alt=""
                                        className="w-full h-full object-cover rounded"
                                      />
                                    ) : (
                                      <Plus className="w-5 h-5 text-gray-500" />
                                    )}
                                  </button>
                                </div>

                                <div className="flex items-center gap-6">
                                  <div className="w-24 px-3 py-1.5 bg-gray-100 rounded text-center font-medium">
                                    {colorInventory}
                                  </div>
                                </div>
                              </div>

                              {expandedColors[color.id] && (
                                <div className="p-5 bg-white">
                                  <table className="w-full text-sm">
                                    <thead>
                                      <tr className="border-b">
                                        <th className="px-4 py-3 text-left font-medium">Size</th>
                                        <th className="px-4 py-3 text-left font-medium">Available</th>
                                        <th className="px-4 py-3 text-left font-medium">SKU</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {selectedSizes.map((size) => {
                                        const key = `${color.id}-${size}`;
                                        const data = variantData[key] || {};
                                        return (
                                          <tr
                                            key={key}
                                            className="border-b last:border-0 hover:bg-gray-50"
                                          >
                                            <td className="px-4 py-3 font-medium">{size}</td>
                                            <td className="px-4 py-3">
                                              <input
                                                value={data.available || ""}
                                                onChange={(e) =>
                                                  updateVariantField(
                                                    key,
                                                    "available",
                                                    e.target.value
                                                  )
                                                }
                                                className="w-24 px-3 py-1.5 border rounded text-center"
                                                type="number"
                                                min="0"
                                                {...disabledProps}
                                              />
                                            </td>
                                            <td className="px-4 py-3">
                                              <input
                                                value={data.code || ""}
                                                onChange={(e) =>
                                                  updateVariantField(
                                                    key,
                                                    "code",
                                                    e.target.value
                                                  )
                                                }
                                                placeholder="Auto"
                                                className="w-48 px-3 py-1.5 border rounded font-mono text-sm"
                                                {...disabledProps}
                                              />
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* SEO */}
              <div className="bg-white rounded-xl shadow-sm border p-5">
                <div className="flex justify-between mb-4">
                  <h3 className="font-medium">Search engine preview</h3>
                  {!isViewMode && (
                    <button
                      onClick={() => setSeoEditOpen(!seoEditOpen)}
                      className="text-blue-600 flex items-center gap-1"
                    >
                      <Edit3 className="w-4 h-4" /> Edit
                    </button>
                  )}
                </div>
                <div className="border rounded p-4 bg-gray-50 text-sm">
                  <p className="text-blue-600">
                    https://lazyx.in/products/{urlHandle || "..."}
                  </p>
                  <h4 className="font-semibold text-blue-700 mt-2">
                    {pageTitle || title}
                  </h4>
                  <p
                    dangerouslySetInnerHTML={{
                      __html: metaDescription || "No description",
                    }}
                  />
                </div>

                {seoEditOpen && (
                  <div className="mt-6 space-y-4">
                    <input
                      value={pageTitle}
                      onChange={(e) => setPageTitle(e.target.value)}
                      placeholder="Page title"
                      className="w-full px-4 py-2 border rounded"
                      {...disabledProps}
                    />
                    <textarea
                      value={metaDescription}
                      onChange={(e) => setMetaDescription(e.target.value)}
                      rows={3}
                      placeholder="Meta description"
                      className="w-full px-4 py-2 border rounded"
                      {...disabledProps}
                    />
                    <input
                      value={urlHandle}
                      onChange={(e) => setUrlHandle(e.target.value)}
                      placeholder="URL handle"
                      className="w-full px-4 py-2 border rounded"
                      {...disabledProps}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <div className="bg-white rounded-xl shadow-sm border p-5">
                <h3 className="font-medium mb-4">Organization</h3>
                <input
                  value={vendor}
                  onChange={(e) => setVendor(e.target.value)}
                  placeholder="Vendor"
                  className="w-full px-4 py-2 border rounded mb-4"
                  {...disabledProps}
                />

                {/* Tags */}
                <div>
                  <label className="block text-sm font-medium mb-2">Tags</label>

                  {!isViewMode && (
                    <div className="flex border rounded mb-3">
                      <input
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            addTag();
                          }
                        }}
                        placeholder="Type and press Enter"
                        className="flex-1 px-4 py-2 outline-none"
                      />
                      <button
                        onClick={addTag}
                        className="px-5 text-blue-600 font-medium"
                      >
                        Add
                      </button>
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2">
                    {tags.map((tag) => {
                      const normalizedTag = tag.trim().toLowerCase();
                      const isMatched = matchedConditionTags.has(normalizedTag);

                      return (
                        <span
                          key={tag}
                          className={`
                            px-4 py-1.5 rounded-full flex items-center gap-2 text-sm font-medium shadow-sm
                            ${isMatched
                              ? "bg-green-100 text-green-800 border-2 border-green-600"
                              : "bg-gray-100 text-gray-700 border border-gray-300"
                            }
                          `}
                        >
                          {tag}
                          {isMatched && (
                            <span className="text-xs bg-green-700 text-white px-2 py-0.5 rounded-full">
                              Collection
                            </span>
                          )}
                          {!isViewMode && (
                            <X
                              className="w-4 h-4 cursor-pointer hover:text-red-600"
                              onClick={() => removeTag(tag)}
                            />
                          )}
                        </span>
                      );
                    })}
                  </div>

                  {matchedConditionTags.size > 0 && (
                    <div className="mt-4 text-xs text-gray-600 bg-blue-50 p-3 rounded-lg">
                      <p className="font-medium text-green-800 mb-2">
                        Collection trigger tags detected:
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {Array.from(matchedConditionTags).map((ctag) => (
                          <span
                            key={ctag}
                            className="bg-green-200 text-green-800 px-2.5 py-1 rounded text-xs"
                          >
                            {ctag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Media Library Modal */}
        {modalOpen && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              <div className="p-5 border-b flex justify-between items-center">
                <h3 className="text-lg font-semibold">
                  {modalMode === "main"
                    ? "Select Main Images"
                    : `Select Images for ${colors.find((c) => c.id === currentColorId)?.name || ""}`}
                </h3>
                <button onClick={() => setModalOpen(false)}>
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6">
                <label className="mb-6 flex flex-col items-center justify-center h-32 border-2 border-dashed rounded-xl cursor-pointer bg-gray-50 hover:bg-gray-100">
                  <ImageIcon className="w-12 h-12 text-gray-400 mb-3" />
                  <span className="text-gray-700">Upload new images</span>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    hidden
                    onChange={handleImageUpload}
                  />
                </label>

                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-4">
                  {allImages.map((img) => (
                    <MediaLibraryImage
                      key={img.id}
                      image={img}
                      isSelected={selectedMedia.includes(img.id)}
                      onToggle={handleMediaSelection}
                      showSelection={true}
                      isViewMode={isViewMode}
                    />
                  ))}
                </div>

                {allImages.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    No images uploaded yet. Start by uploading some images above.
                  </div>
                )}
              </div>

              <div className="p-5 border-t flex justify-between items-center bg-gray-50">
                <div className="text-sm text-gray-600">
                  {selectedMedia.length} image{selectedMedia.length !== 1 ? "s" : ""} selected
                </div>
                <div className="flex gap-4">
                  <button
                    onClick={() => setModalOpen(false)}
                    className="px-6 py-2 border rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveMediaSelection}
                    className="px-8 py-2 bg-black text-white rounded-lg font-medium hover:bg-gray-800"
                  >
                    Save Selection
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Fixed Save Button */}
        {!isViewMode && (
          <div className=" bg-white border-t px-6 py-4 flex justify-end gap-4 z-50">
            <Link to="/allproducts" className="px-6 py-3 border rounded-lg text-sm hover:bg-gray-50">
              Discard
            </Link>
            <button
              onClick={handleSaveProduct}
              className="px-6 py-2.5 bg-[var(--primary-color)] text-white rounded-lg hover:bg-[var(--primary-hover)] text-sm font-medium"
            >
              Save Product
            </button>
          </div>
        )}
      </div>
    </MasterLayout>
  );
}